import {
  IApplicationContact,
  IApplicationResident,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";

export const getTotalIncome = (contacts: IApplicationContact[]) =>
  contacts.reduce((contactAcc: number, contactCurr: any) => {
    const incomeValue = contactCurr.income.reduce(
      (incomeAcc: number, incomeCurr: any) => incomeAcc + incomeCurr.value,
      0,
    );
    return contactAcc + incomeValue;
  }, 0);

export const getResidentsCount = (
  residents: IApplicationResident[],
  residentId: string,
) => {
  return residents.reduce(
    (currentCount, resident) =>
      resident.id === residentId ? currentCount + resident.value : currentCount,
    0,
  );
};

/**
 * Find common numbers in multiple arrays.
 * @param arrays - Arrays containing numbers.
 * @returns An array containing numbers that appear in each input array.
 */
export const findCommonNumbers = (...arrays: number[][]): number[] => {
  if (arrays.length === 0) {
    return [];
  }

  // Use the first array as a reference
  const referenceArray = arrays[0];

  // Filter numbers that appear in all arrays
  const commonNumbers = referenceArray.filter(num =>
    arrays.every(array => array.includes(num)),
  );

  return commonNumbers;
};

const getLocalizedLangName = (lang: string) => {
  return getLocalizedText(`application.contact_language.${lang}`);
};

export const getLanguage = (locale: string) => {
  switch (locale) {
    case ELocale.NL:
      return getLocalizedLangName("nl");
    case ELocale.FR:
      return getLocalizedLangName("fr");
    case ELocale.EN:
      return getLocalizedLangName("en");
    default:
      return getLocalizedLangName("en");
  }
};

/**
 * The property's availability date in the shape the score util expects, or
 * null when the property has none.
 *
 * The raw date is passed on unchanged, exactly as rentmatch does on the
 * candidate side: both sides have to produce the same score today (decision on
 * DEV-3056). So a property that becomes available later than the candidate
 * wants to move in scores lower here too, which is intended.
 * A value that cannot be read as a date (the MySQL zero date) is handled
 * inside the score util itself, which treats it as "no date".
 */
export const getScoreAvailableDate = (property?: {
  availableAtDate?: string | Date;
}): string | null => {
  const availableAtDate = property?.availableAtDate;
  if (!availableAtDate) {
    return null;
  }
  return availableAtDate instanceof Date
    ? availableAtDate.toISOString()
    : String(availableAtDate);
};

/**
 * The candidate's move-in intent in the shape the score util expects.
 *
 * Mirrors the derivation on the candidate side (rentmatch,
 * views/Profile/RentMatches.tsx) and in the mail worker: a desired date within
 * a week reads as "as soon as possible", anything further out is a specific
 * date. The dashboard used to map every filled date to "specific", which put
 * the same candidate in a different band here than in the mail, because the two
 * branches of the date score use different tolerances (30 days against 3). See
 * DEV-3056.
 *
 * The date is handed over as YYYY-MM-DD, which is what the score util's
 * interface documents.
 */
export const getScoreMoveInDate = (
  dateOfOccupancy?: string | Date | null,
): {
  moveInDateType: "flexible" | "immediately" | "specific";
  specificMoveInDate?: string;
} => {
  if (!dateOfOccupancy) {
    return { moveInDateType: "flexible" };
  }

  const wanted = new Date(dateOfOccupancy);
  if (Number.isNaN(wanted.getTime())) {
    return { moveInDateType: "flexible" };
  }

  const daysUntilWanted = (wanted.getTime() - Date.now()) / 86400000;
  if (daysUntilWanted <= 7) {
    return { moveInDateType: "immediately" };
  }

  return {
    moveInDateType: "specific",
    specificMoveInDate: wanted.toISOString().slice(0, 10),
  };
};
